/**
 * Timesheet Fill Rate Card
 *
 * Shows the % of useful hours that were filled in the timesheet for a period,
 * with a PROA target marker at 80%.
 *
 * Inputs:
 *  - startDate / endDate: ISO date strings (YYYY-MM-DD)
 *  - horasPreenchidas: total hours actually filled in the timesheet (from backend)
 *  - hoursPerDay: useful hours per business day (default 8)
 *  - proaTarget: target fill-rate % (default 80)
 *
 * NOTE: `horasPreenchidas` is currently passed as a prop and can be mocked.
 * The backend endpoint `dre.getClientMatrix` does not yet expose this field —
 * once it does, replace the mock with the real total.
 */

import { useMemo } from 'react'
import { SensitiveValue } from '@/components/privacy/SensitiveText'

const CIRCLE_SIZE = 160
const STROKE_WIDTH = 2
const RADIUS = (CIRCLE_SIZE - STROKE_WIDTH) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

/**
 * Compute Easter Sunday date for a given year (Meeus/Jones/Butcher algorithm).
 * Returns a Date at local midnight.
 */
function easterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function addDays(d: Date, days: number): Date {
  const copy = new Date(d)
  copy.setDate(copy.getDate() + days)
  return copy
}

/**
 * Brazilian national holidays for a given year — fixed + Easter-derived + Consciência Negra.
 * Includes Carnaval (Mon+Tue) as it's effectively observed nationally.
 */
function brazilianNationalHolidays(year: number): Set<string> {
  const easter = easterSunday(year)
  const holidays: Date[] = [
    new Date(year, 0, 1),           // Confraternização Universal
    addDays(easter, -48),           // Segunda de Carnaval
    addDays(easter, -47),           // Terça de Carnaval
    addDays(easter, -2),            // Sexta-feira Santa
    new Date(year, 3, 21),          // Tiradentes
    new Date(year, 4, 1),           // Dia do Trabalho
    addDays(easter, 60),            // Corpus Christi
    new Date(year, 8, 7),           // Independência
    new Date(year, 9, 12),          // Nossa Senhora Aparecida
    new Date(year, 10, 2),          // Finados
    new Date(year, 10, 15),         // Proclamação da República
    new Date(year, 10, 20),         // Consciência Negra (lei 14.759/2023)
    new Date(year, 11, 25),         // Natal
  ]
  return new Set(holidays.map(toIsoDate))
}

/**
 * Count business days (Mon-Fri, excluding Brazilian national holidays)
 * between two ISO dates, inclusive.
 */
export function countBusinessDays(startIso: string, endIso: string): number {
  if (!startIso || !endIso) return 0
  const start = new Date(startIso + 'T00:00:00')
  const end = new Date(endIso + 'T00:00:00')
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0
  if (end < start) return 0

  // Pre-compute holiday sets for each year in the range (usually 1 or 2 years).
  const holidays = new Set<string>()
  for (let y = start.getFullYear(); y <= end.getFullYear(); y += 1) {
    for (const iso of brazilianNationalHolidays(y)) holidays.add(iso)
  }

  let days = 0
  const current = new Date(start)
  while (current <= end) {
    const day = current.getDay() // 0 = Sun, 6 = Sat
    if (day !== 0 && day !== 6 && !holidays.has(toIsoDate(current))) {
      days += 1
    }
    current.setDate(current.getDate() + 1)
  }
  return days
}

/** Compact R$ formatter: R$ 1,2M / R$ 350K / R$ 900 */
function fmtCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `R$ ${(abs / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`
  if (abs >= 1_000) return `R$ ${(abs / 1_000).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}K`
  return `R$ ${abs.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

/** Pace status for timesheet fill rate */
function timesheetPaceStatus(percent: number): {
  label: string
  color: string
  bg: string
  dot: string
  border: string
} {
  if (percent >= 80) return {
    label: 'Acima do pace',
    color: 'text-brand-primary',
    bg: 'bg-brand-primary/10',
    dot: 'bg-brand-primary',
    border: 'border border-transparent',
  }
  if (percent >= 70) return {
    label: 'No pace',
    color: 'text-brand-primary',
    bg: 'bg-transparent',
    dot: 'bg-brand-primary',
    border: 'border border-brand-primary/30',
  }
  return {
    label: 'Abaixo do pace',
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    dot: 'bg-red-400',
    border: 'border border-transparent',
  }
}

interface TimesheetFillRateCardProps {
  startDate: string
  endDate: string
  horasPreenchidas: number
  hoursPerDay?: number
  proaTarget?: number
  /** Folha YTD value (absolute) — used to calculate untracked payroll */
  folhaReal?: number
}

export function TimesheetFillRateCard({
  startDate,
  endDate,
  horasPreenchidas,
  hoursPerDay = 8,
  proaTarget = 80,
  folhaReal,
}: TimesheetFillRateCardProps) {
  const { percent, targetAngle } = useMemo(() => {
    const businessDays = countBusinessDays(startDate, endDate)
    const totalUseful = businessDays * hoursPerDay
    const pct = totalUseful > 0 ? (horasPreenchidas / totalUseful) * 100 : 0
    // Angle of the PROA target mark along the circle, in degrees.
    const angle = (proaTarget / 100) * 360
    return { percent: pct, targetAngle: angle }
  }, [startDate, endDate, horasPreenchidas, hoursPerDay, proaTarget])

  const clamped = Math.min(100, Math.max(0, percent))
  const offset = CIRCUMFERENCE - (clamped / 100) * CIRCUMFERENCE
  const hitsTarget = percent >= proaTarget
  const isCritical = percent < 50
  // Progress color: green when ≥ PROA, red when < 50%, gray otherwise.
  const color = hitsTarget ? '#D2FA52' : isCritical ? '#F87171' : '#6B7280'
  const valueClass = hitsTarget
    ? 'text-brand-primary'
    : isCritical
      ? 'text-red-400'
      : 'text-white'

  // Target marker position: compute a dot centered on the ring.
  // Rotate from 12 o'clock (−90°), matching the progress arc.
  const targetRad = ((targetAngle - 90) * Math.PI) / 180
  const cx = CIRCLE_SIZE / 2
  const cy = CIRCLE_SIZE / 2
  const dotX = cx + RADIUS * Math.cos(targetRad)
  const dotY = cy + RADIUS * Math.sin(targetRad)
  // Label sits just outside the ring (svg has overflow visible so it is not clipped).
  const labelRadius = RADIUS + 18
  const labelX = cx + labelRadius * Math.cos(targetRad)
  const labelY = cy + labelRadius * Math.sin(targetRad)

  const pace = timesheetPaceStatus(percent)
  const untrackedValue = folhaReal && folhaReal > 0
    ? folhaReal * (1 - clamped / 100)
    : null

  return (
    <div className="flex flex-col items-center justify-center py-3 gap-4">
      <div
        className="relative flex-shrink-0"
        style={{ width: CIRCLE_SIZE, height: CIRCLE_SIZE }}
      >
        <svg width={CIRCLE_SIZE} height={CIRCLE_SIZE} style={{ overflow: 'visible' }}>
          <g className="-rotate-90 origin-center" style={{ transformBox: 'fill-box' }}>
            {/* Background ring */}
            <circle
              cx={cx}
              cy={cy}
              r={RADIUS}
              stroke="#2A2D34"
              strokeWidth={STROKE_WIDTH}
              fill="none"
            />
            {/* Progress arc */}
            <circle
              cx={cx}
              cy={cy}
              r={RADIUS}
              stroke={color}
              strokeWidth={STROKE_WIDTH}
              fill="none"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.6s ease-out' }}
            />
          </g>
          {/* PROA target dot (not rotated — absolute position) */}
          <circle
            cx={dotX}
            cy={dotY}
            r={3}
            fill="#9CA3AF"
            opacity={0.9}
          />
          {/* PROA target label beside the tick */}
          <text
            x={labelX}
            y={labelY}
            fill="#9CA3AF"
            fontSize={10}
            fontWeight={600}
            textAnchor="middle"
            dominantBaseline="central"
            opacity={0.9}
          >
            {proaTarget}%
          </text>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <span className="text-[10px] sm:text-[11px] font-semibold text-brand-gray uppercase tracking-wider">
            Timesheet
          </span>
          <span className={`text-3xl sm:text-4xl font-bold font-display ${valueClass}`}>
            {percent.toFixed(0)}%
          </span>
          <span className="text-[9px] sm:text-[10px] text-brand-gray uppercase tracking-wider mt-0.5 leading-tight">
            Alvo ({proaTarget}%)
          </span>
        </div>
      </div>

      {/* Pace badge */}
      <div className="flex flex-col items-center gap-1.5">
        <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md ${pace.bg} ${pace.border}`}>
          <span className="relative inline-flex w-1.5 h-1.5">
            <span className={`absolute inline-flex h-full w-full rounded-full ${pace.dot} opacity-75 animate-ping`} />
            <span className={`relative inline-flex w-1.5 h-1.5 rounded-full ${pace.dot}`} />
          </span>
          <span className={`text-[11px] font-semibold ${pace.color}`}>
            {pace.label}
          </span>
        </div>
        {untrackedValue !== null && (
          <span className="text-[11px] text-brand-gray text-center">
            <SensitiveValue mask="R$ ••••••">
              {fmtCompact(untrackedValue)}
            </SensitiveValue>
            {' '}da Folha sem registro
          </span>
        )}
      </div>
    </div>
  )
}
